import { useAtomValue } from 'jotai'
import { metadataChannelAtom } from '@/atoms/invalidation'
import { addUsernameToIndex } from '@/services/client.service'
import { loadNostrUser, bareNostrUser, NostrUser } from '@nostr/gadgets/metadata'
import { useMemo } from 'react'
import { useCurrentProfile } from '@/atoms/profile'
import { resolveProfileInput } from '@/lib/pubkey'
import { useAsync } from 'react-use'

export function useFetchProfile(input: string | NostrUser) {
  const currentAccountProfile = useCurrentProfile()
  const channel = useAtomValue(metadataChannelAtom)

  const { value: req } = useAsync(
    async () => (typeof input === 'object' ? input : resolveProfileInput(input)),
    [input]
  )

  const fallback: NostrUser | undefined = useMemo((): NostrUser => {
    if (typeof input === 'object') return input
    if (req) {
      if ('shortName' in req) return req
      else return bareNostrUser(req.pubkey)
    } else
      return {
        pubkey: '0000000000000000000000000000000000000000000000000000000000000000',
        shortName: input,
        lastUpdated: 0,
        metadata: {},
        npub: input
      }
  }, [req])

  const {
    error,
    value: profile,
    loading: isFetching
  } = useAsync(async () => {
    if (!req) return
    if (typeof req === 'object' && 'shortName' in req) return req

    if (currentAccountProfile && fallback.pubkey === currentAccountProfile.pubkey)
      return currentAccountProfile

    const profile = await loadNostrUser(req)
    if (profile) {
      addUsernameToIndex(profile)
      return profile
    }
  }, [req, currentAccountProfile, channel])

  return { isFetching, error, profile: profile ?? fallback }
}
